import React, { useState, useEffect } from "react";
import { RouteComponentProps } from "react-router";
import QuizComponent, { AnswerResult, isLastQuestion, getQuestion, isQuestionValid } from "@upandgo/react-quiz";
import { MainResource, SectionQuiz, SavedSection, Section } from "../../types";
import {
  MainResourceItem,
  SecondaryResourceItem,
  Switch,
  Drawer,
  SectionVideoDrawer,
  StandardButton,
} from "../../components";
import {
  QuizResultContainer,
  QuizMainResourcesContainer,
  QuizTitle,
  QuizSecondaryResourcesContainer,
  ShowAllContainer,
  SwitchLabel,
  QuizContainer,
  SectionDescription,
} from "./Quiz.style";
import { theme } from "../../Theme";
import Store from "../../state/store";
import { inject, observer } from "mobx-react";
import Actions from "../../state/actions";
import { findItemAndIndexById } from "../../utils";
import { Link } from "react-router-dom";

interface Props extends RouteComponentProps {
  idSection: string;
  store?: Store;
  mutator?: Actions;
}

const OnlyQuizView = (props: { section: SectionQuiz; onAnswerQuestion: (result: AnswerResult) => any }) => {
  return (
    <QuizComponent
      theme={{
        primaryColor: theme.color.primary,
      }}
      questionAggregate={props.section.quiz}
      onAnswerQuestion={props.onAnswerQuestion}
    />
  );
};

const CustomResourcesView = ({
  section,
  savedSection,
  updateSavedSection,
}: {
  section: SectionQuiz;
  savedSection: SavedSection;
  updateSavedSection: (
    idSection: string,
    save: Partial<Pick<SavedSection, "done" | "validQuestionList" | "primaryResourceSeen" | "secondaryResourceSeen">>,
  ) => Promise<void>;
}) => {
  const [showAll, setShowAll] = useState(false);

  if (section.outro.type === "resources") {
    const shouldMainResourceBeDisplayed = (resource: MainResource) => {
      if (showAll) return true;
      for (let i = 0, n = resource.hideByQuestionValidation.length; i < n; i++) {
        if (savedSection.validQuestionList.includes(resource.hideByQuestionValidation[i])) return false;
        const question = getQuestion(section.quiz, resource.hideByQuestionValidation[i]);
        if (isQuestionValid(question.item)) return false;
      }
      return true;
    };

    const onClickMainResource = (id: string) => {
      updateSavedSection(section.id, {
        primaryResourceSeen: [...savedSection.primaryResourceSeen, id],
      });
    };
    const onClickSecondaryResource = (id: string) => {
      if (section.outro.type === "resources") {
        updateSavedSection(section.id, {
          secondaryResourceSeen: [...savedSection.secondaryResourceSeen, id],
        });
        const res = section.outro.secondaryResourceList.find((res) => res.id === id);
        if (res) window.open(res.url, "_blank");
      }
    };
    const onClickSwitchShowAll = () => {
      setShowAll(!showAll);
    };
    return (
      <QuizResultContainer>
        <ShowAllContainer>
          <SwitchLabel>Toutes les vidéos</SwitchLabel>
          <Switch checked={showAll} onClick={onClickSwitchShowAll} />
        </ShowAllContainer>
        <QuizMainResourcesContainer>
          {section.outro.mainResourceList.filter(shouldMainResourceBeDisplayed).map((resource) => (
            <MainResourceItem
              key={resource.id}
              id={resource.id}
              title={resource.title}
              description={resource.description}
              onClick={onClickMainResource}
              image={resource.image}
              seen={savedSection.primaryResourceSeen.includes(resource.id)}
            />
          ))}
        </QuizMainResourcesContainer>
        {section.outro.secondaryResourceList.length > 0 && (
          <QuizSecondaryResourcesContainer>
            <QuizTitle>Ressources complémentaires</QuizTitle>
            {section.outro.secondaryResourceList.map((resource) => (
              <SecondaryResourceItem
                key={resource.id}
                id={resource.id}
                title={resource.title}
                description={resource.description}
                onClick={onClickSecondaryResource}
              />
            ))}
          </QuizSecondaryResourcesContainer>
        )}
      </QuizResultContainer>
    );
  }
  return null;
};

const CongratsView = (props: { title: string; description: string; nextSection?: Section }) => {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <h1>{props.title}</h1>
      <span>{props.description}</span>
      {props.nextSection && (
        <div style={{ marginTop: "3em" }}>
          <Link to={`/section/${props.nextSection.id}`}>
            <StandardButton textButton={props.nextSection.title} />
          </Link>
        </div>
      )}
    </div>
  );
};

const QuizView: React.FC<Props> = (props) => {
  const sectionItemAndIndex = findItemAndIndexById(props.idSection, props.store!.content.sections);
  const section = sectionItemAndIndex.item as SectionQuiz;
  const nextSection = props.store!.content.sections[sectionItemAndIndex.index + 1];

  const { savedSections } = props.store!;
  const savedSection = savedSections[props.idSection] || {
    done: false,
    primaryResourceSeen: [],
    secondaryResourceSeen: [],
    validQuestionList: [],
  };
  const [step, setStep] = useState(0);

  useEffect(() => {
    props.mutator!.fetchSavedSection();
  }, []);

  useEffect(() => {
    if (savedSections[props.idSection] && savedSections[props.idSection].done) setStep(1);
  }, [savedSections]);

  const onAnswerQuestion = (result: AnswerResult) => {
    props.mutator!.selectAnswers(props.idSection, result.idQuestion, result.idAnswers);
    if (isLastQuestion(section.quiz, result.idQuestion)) {
      setStep(1);
      props.mutator!.updateSavedSection(props.idSection, { done: true });
    }
  };

  return (
    <QuizContainer>
      {step === 0 && <OnlyQuizView section={section} onAnswerQuestion={onAnswerQuestion} />}
      {step === 1 && section.outro.type === "resources" && (
        <CustomResourcesView
          section={section}
          savedSection={savedSection}
          updateSavedSection={props.mutator!.updateSavedSection}
        />
      )}
      {step === 1 && section.outro.type === "congrats" && (
        <CongratsView title={section.outro.title} description={section.outro.description} nextSection={nextSection} />
      )}
    </QuizContainer>
  );
};

export default inject("store", "mutator")(observer(QuizView));
